import { memo, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertTriangle, Clock, DollarSign, FileText } from "lucide-react";
import {
  useCollectionPerformance,
  useOrganizationDSO,
} from "../../api/queries/dashboardQueries";
import { displayCurrencyAmount } from "../../utils/display";
import {
  formatCollectionPeriodLabel,
  PERIOD_GROUPING_OPTIONS,
  type PeriodGrouping,
} from "../../utils/periodGrouping";
import { KpiCard } from "../KpiCard";
import { useAuth } from "../../hooks/useAuth";
import {
  LineChartEmptyContent,
} from "../datamintEmptyStates";
import { DashboardZone } from "../Dashboards/DashboardZone";
import type { ReceivablesGroupAgingBreakdown } from "../../api/queries/receivablesQueries";
import type { AgingBucket } from "../../utils/receivablesAgingBuckets";
import { ReceivablesAgingBreakdownCard } from "./ReceivablesAgingBreakdownCard";

type DebtChartPoint = {
  month: string;
  collected: number;
  openBalance: number;
  overdue: number;
  collectedPct: number;
  currentPct: number;
  overduePct: number;
  collectionRate: number;
};

type DebtChartTooltipProps = {
  active?: boolean;
  payload?: ReadonlyArray<{
    dataKey?: string | number;
    value?: number;
    payload: DebtChartPoint;
  }>;
  label?: string;
  currency: string;
  asPercent: boolean;
};

function payloadSeriesValue(
  payload: ReadonlyArray<{ dataKey?: string | number; value?: number }>,
  dataKey: string,
  fallback: number,
): number {
  const entry = payload.find((item) => item.dataKey === dataKey);
  if (entry != null && typeof entry.value === "number" && !Number.isNaN(entry.value)) {
    return entry.value;
  }
  return fallback;
}

function ReceivablesDebtChartTooltip({
  active,
  payload,
  label,
  currency,
  asPercent,
}: DebtChartTooltipProps) {
  if (!active || !payload?.length) return null;

  const data = payload[0].payload;
  const collected = asPercent
    ? payloadSeriesValue(payload, "collectedPct", data.collectedPct)
    : payloadSeriesValue(payload, "collected", data.collected);
  const current = asPercent
    ? payloadSeriesValue(payload, "currentPct", data.currentPct)
    : payloadSeriesValue(payload, "openBalance", data.openBalance);
  const overdue = asPercent
    ? payloadSeriesValue(payload, "overduePct", data.overduePct)
    : payloadSeriesValue(payload, "overdue", data.overdue);
  const stackTotal = collected + current + overdue;
  const collectionRate = asPercent
    ? stackTotal > 0
      ? (collected / stackTotal) * 100
      : payloadSeriesValue(payload, "collectionRate", data.collectionRate)
    : payloadSeriesValue(
        payload,
        "collectionRate",
        stackTotal > 0 ? (collected / stackTotal) * 100 : data.collectionRate,
      );

  const rows = asPercent
    ? [
        {
          label: "Collected",
          value: `${collected.toFixed(0)}%`,
          color: "text-[#4B7F73]",
        },
        {
          label: "Current",
          value: `${current.toFixed(0)}%`,
          color: "text-[#6B7280]",
        },
        {
          label: "Overdue",
          value: `${overdue.toFixed(0)}%`,
          color: "text-[#D97706]",
        },
        {
          label: "Collection Rate",
          value: `${collectionRate.toFixed(0)}%`,
          color: "text-[#2563EB]",
        },
      ]
    : [
        {
          label: "Collected",
          value: displayCurrencyAmount(collected, currency),
          color: "text-[#4B7F73]",
        },
        {
          label: "Current",
          value: displayCurrencyAmount(current, currency),
          color: "text-[#6B7280]",
        },
        {
          label: "Overdue",
          value: displayCurrencyAmount(overdue, currency),
          color: "text-[#D97706]",
        },
        {
          label: "Collection Rate",
          value: `${collectionRate.toFixed(0)}%`,
          color: "text-[#2563EB]",
        },
      ];

  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white/95 px-3 py-2 text-xs shadow-lg backdrop-blur-sm">
      <p className="mb-1.5 font-medium text-[#111827]">{label}</p>
      <div className="space-y-1">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between gap-4">
            <span className={row.color}>{row.label}</span>
            <span className="tabular-nums font-medium text-[#111827]">
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

type ChartManagerFilters = {
  collectorId?: number;
  customerGroupId?: number;
};

type ReceivablesChartsPanelProps = {
  currency: string;
  consolidated?: boolean;
  startDate?: string;
  endDate?: string;
  managerFilters?: ChartManagerFilters;
  receivablesDisplayCurrency: string;
  unpaidInvoicesCount: number;
  openBalanceAmount: number;
  criticalAmount: number;
  criticalInvoicesCount: number;
  agingBuckets: AgingBucket[];
  groupAgingBreakdowns?: ReceivablesGroupAgingBreakdown[];
  /**
   * Period granularity, owned by the dashboard's single filter row so every
   * time-series card moves together. Falls back to local state when this
   * panel is used somewhere without a shared control.
   */
  grouping?: PeriodGrouping;
};

function ReceivablesChartsPanelComponent({
  currency,
  consolidated = false,
  startDate,
  endDate,
  managerFilters,
  receivablesDisplayCurrency,
  unpaidInvoicesCount,
  openBalanceAmount,
  criticalAmount,
  criticalInvoicesCount,
  agingBuckets,
  groupAgingBreakdowns,
  grouping,
}: ReceivablesChartsPanelProps) {
  const { hasFeature } = useAuth();
  const showChartAsPercent = hasFeature("RECEIVABLES_PERCENT_CHART_VIEW");
  const [localGrouping, setLocalGrouping] = useState<PeriodGrouping>("month");
  const isControlled = grouping != null;
  const collectionGrouping = grouping ?? localGrouping;
  const setCollectionGrouping = setLocalGrouping;

  const collectionPerformanceQuery = useCollectionPerformance(
    currency,
    startDate,
    endDate,
    collectionGrouping,
    "issue_date",
    managerFilters,
    consolidated,
  );
  const dsoQuery = useOrganizationDSO(
    currency,
    startDate,
    endDate,
    managerFilters,
    consolidated,
  );

  const debtChartLoading = collectionPerformanceQuery.isPending;

  const debtChartData = useMemo(() => {
    const performance = collectionPerformanceQuery.data?.performance ?? [];
    return performance.map((point) => {
      const label = formatCollectionPeriodLabel(
        point.period,
        collectionGrouping,
      );
      const total =
        point.collected_amount + point.current_amount + point.overdue_amount;
      const collectionRate =
        total > 0 ? (point.collected_amount / total) * 100 : 0;
      return {
        month: label,
        collected: point.collected_amount,
        openBalance: point.current_amount,
        overdue: point.overdue_amount,
        collectedPct: total > 0 ? (point.collected_amount / total) * 100 : 0,
        currentPct: total > 0 ? (point.current_amount / total) * 100 : 0,
        overduePct: total > 0 ? (point.overdue_amount / total) * 100 : 0,
        collectionRate,
      };
    });
  }, [collectionPerformanceQuery.data?.performance, collectionGrouping]);

  const totalCollected = debtChartData.reduce(
    (sum, item) => sum + item.collected,
    0,
  );
  const totalOpenBalanceChart = debtChartData.reduce(
    (sum, item) => sum + item.openBalance,
    0,
  );
  const totalOverdue = debtChartData.reduce((sum, item) => sum + item.overdue, 0);
  const grandTotal = totalCollected + totalOpenBalanceChart + totalOverdue;
  const overallCollectionRate =
    grandTotal > 0 ? (totalCollected / grandTotal) * 100 : 0;
  const overallCollectedPct = overallCollectionRate;
  const overallCurrentPct =
    grandTotal > 0 ? (totalOpenBalanceChart / grandTotal) * 100 : 0;
  const overallOverduePct =
    grandTotal > 0 ? (totalOverdue / grandTotal) * 100 : 0;

  const dsoDays = Math.round(dsoQuery.data?.dso_days ?? 0);

  const dsoTrend = (() => {
    const invoiceCount = dsoQuery.data?.total_invoice_count ?? 0;
    const daysSales = dsoQuery.data?.days_sales ?? 60;
    const periodDays = dsoQuery.data?.period_days ?? 45;
    if (invoiceCount === 0) {
      return `No credit sales in last ${daysSales} days`;
    }
    return `${daysSales}-day sales · ${periodDays}-day period · ${invoiceCount} invoice${invoiceCount === 1 ? "" : "s"}`;
  })();

  // When the dashboard owns grouping, this panel must not render a second
  // control for it — one filter row scopes everything below it.
  const periodGroupingControl = isControlled ? null : (
    <div
      role="group"
      aria-label="Collection period grouping"
      className="inline-flex rounded-lg border border-[#E5E7EB] bg-[#F3F4F6] p-0.5"
    >
      {PERIOD_GROUPING_OPTIONS.map((option) => {
        const selected = option.value === collectionGrouping;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            onClick={() => setCollectionGrouping(option.value)}
            className={
              selected
                ? "rounded-md bg-white px-3 py-1.5 text-sm font-medium text-[#111827] shadow-sm"
                : "rounded-md px-3 py-1.5 text-sm font-medium text-[#6B7280] hover:text-[#111827]"
            }
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  const legendItems = showChartAsPercent
    ? [
        { label: "Collected", color: "#4B7F73", value: `${overallCollectedPct.toFixed(0)}%` },
        { label: "Current", color: "#94a3b8", value: `${overallCurrentPct.toFixed(0)}%` },
        { label: "Overdue", color: "#D97706", value: `${overallOverduePct.toFixed(0)}%` },
      ]
    : [
        {
          label: "Collected",
          color: "#4B7F73",
          value: displayCurrencyAmount(totalCollected, receivablesDisplayCurrency),
        },
        {
          label: "Current",
          color: "#94a3b8",
          value: displayCurrencyAmount(
            totalOpenBalanceChart,
            receivablesDisplayCurrency,
          ),
        },
        {
          label: "Overdue",
          color: "#D97706",
          value: displayCurrencyAmount(totalOverdue, receivablesDisplayCurrency),
        },
        {
          label: "Collection rate",
          color: "#2563EB",
          value: `${overallCollectionRate.toFixed(0)}%`,
        },
      ];

  return (
    <div className="space-y-8">
      <DashboardZone
        title="Risk overview"
        description="Headline exposure across unpaid invoices, aging risk, and DSO"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            title="Total open balance"
            value={displayCurrencyAmount(
              openBalanceAmount,
              receivablesDisplayCurrency,
            )}
            hint="Outstanding receivables"
            Icon={DollarSign}
            iconBgClass="bg-[#EEF4F2]"
            iconColorClass="text-[#4B7F73]"
            hintClassName="mt-1 text-sm text-[#6B7280]"
            titleClassName="text-sm font-medium text-[#6B7280]"
            valueClassName="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-[#111827]"
            className="border-[#E5E7EB] border-l-4 border-l-[#4B7F73]"
          />
          <KpiCard
            title="Critical (90+ days)"
            value={displayCurrencyAmount(
              criticalAmount,
              receivablesDisplayCurrency,
            )}
            hint={`${criticalInvoicesCount} invoice${criticalInvoicesCount === 1 ? "" : "s"} need attention`}
            Icon={AlertTriangle}
            iconBgClass="bg-[#FEE2E2]"
            iconColorClass="text-[#DC2626]"
            hintClassName="mt-1 text-sm text-[#DC2626]"
            titleClassName="text-sm font-medium text-[#6B7280]"
            valueClassName="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-[#111827]"
            className="border-[#E5E7EB] border-l-4 border-l-[#DC2626]"
          />
          <KpiCard
            title="Unpaid invoices"
            value={`${unpaidInvoicesCount}`}
            hint="Across selected filters"
            Icon={FileText}
            iconBgClass="bg-[#F3F4F6]"
            iconColorClass="text-[#6B7280]"
            hintClassName="mt-1 text-sm text-[#6B7280]"
            titleClassName="text-sm font-medium text-[#6B7280]"
            valueClassName="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-[#111827]"
            className="border-[#E5E7EB]"
          />
          <KpiCard
            title="DSO"
            value={`${dsoDays}`}
            hint={dsoTrend}
            Icon={Clock}
            iconBgClass="bg-[#EEF4F2]"
            iconColorClass="text-[#4B7F73]"
            hintClassName="mt-1 text-sm text-[#6B7280]"
            titleClassName="text-sm font-medium text-[#6B7280]"
            valueClassName="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-[#111827]"
            className="border-[#E5E7EB]"
          />
        </div>
      </DashboardZone>

      <DashboardZone
        title="Collection & aging"
        description="Where balances sit and how collections perform over time"
        actions={periodGroupingControl}
      >
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <article className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-base font-semibold text-[#111827]">
              Collection performance
            </h3>
            <div className="min-h-[280px] w-full">
              {debtChartLoading ? (
                <div className="flex h-72 w-full animate-pulse rounded-lg bg-[#F3F4F6]" />
              ) : debtChartData.length === 0 ? (
                <LineChartEmptyContent
                  ctaTo="/files-integrations"
                  legend={
                    showChartAsPercent
                      ? [
                          { code: "Collected", color: "#4B7F73" },
                          { code: "Current", color: "#94a3b8" },
                          { code: "Overdue", color: "#D97706" },
                        ]
                      : [
                          { code: "Collected", color: "#4B7F73" },
                          { code: "Current", color: "#94a3b8" },
                          { code: "Overdue", color: "#D97706" },
                          { code: "Collection rate", color: "#2563EB" },
                        ]
                  }
                />
              ) : (
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={debtChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12, fill: "#6B7280" }}
                      />
                      {showChartAsPercent ? (
                        <>
                          <YAxis
                            domain={[0, 100]}
                            tick={{ fontSize: 12, fill: "#6B7280" }}
                            tickFormatter={(value) => `${Math.round(value)}%`}
                          />
                          <Tooltip
                            content={
                              <ReceivablesDebtChartTooltip
                                currency={receivablesDisplayCurrency}
                                asPercent
                              />
                            }
                          />
                          <Bar isAnimationActive={false}
                            dataKey="currentPct"
                            stackId="debt"
                            fill="#94a3b8"
                            name="Current"
                          />
                          <Bar isAnimationActive={false}
                            dataKey="collectedPct"
                            stackId="debt"
                            fill="#4B7F73"
                            name="Collected"
                          />
                          <Bar isAnimationActive={false}
                            dataKey="overduePct"
                            stackId="debt"
                            fill="#D97706"
                            name="Overdue"
                            radius={[4, 4, 0, 0]}
                          />
                        </>
                      ) : (
                        <>
                          <YAxis
                            yAxisId="amount"
                            tick={{ fontSize: 12, fill: "#6B7280" }}
                            tickFormatter={(value) =>
                              `${Math.round(value / 1000)}K`
                            }
                          />
                          <YAxis
                            yAxisId="rate"
                            orientation="right"
                            tick={{ fontSize: 12, fill: "#6B7280" }}
                            tickFormatter={(value) => `${Math.round(value)}%`}
                          />
                          <Tooltip
                            content={
                              <ReceivablesDebtChartTooltip
                                currency={receivablesDisplayCurrency}
                                asPercent={false}
                              />
                            }
                          />
                          <Bar isAnimationActive={false}
                            yAxisId="amount"
                            dataKey="openBalance"
                            stackId="debt"
                            fill="#94a3b8"
                            name="Current"
                          />
                          <Bar isAnimationActive={false}
                            yAxisId="amount"
                            dataKey="collected"
                            stackId="debt"
                            fill="#4B7F73"
                            name="Collected"
                          />
                          <Bar isAnimationActive={false}
                            yAxisId="amount"
                            dataKey="overdue"
                            stackId="debt"
                            fill="#D97706"
                            name="Overdue"
                            radius={[4, 4, 0, 0]}
                          />
                          <Line isAnimationActive={false}
                            yAxisId="rate"
                            type="monotone"
                            dataKey="collectionRate"
                            stroke="#2563EB"
                            strokeWidth={2}
                            dot={false}
                            name="Collection Rate"
                          />
                        </>
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            {debtChartData.length > 0 ? (
              <ul className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
                {legendItems.map((item) => (
                  <li
                    key={item.label}
                    className="inline-flex items-center gap-2 text-sm text-[#111827]"
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: item.color }}
                      aria-hidden
                    />
                    <span className="text-[#6B7280]">{item.label}</span>
                    <span className="tabular-nums font-medium">{item.value}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </article>

          <ReceivablesAgingBreakdownCard
            agingBuckets={agingBuckets}
            openBalanceAmount={openBalanceAmount}
            displayCurrency={receivablesDisplayCurrency}
          />
        </div>
        {groupAgingBreakdowns != null && groupAgingBreakdowns.length > 0 ? (
          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
            {groupAgingBreakdowns.map((group) => (
              <ReceivablesAgingBreakdownCard
                key={group.groupId}
                title={group.groupName}
                agingBuckets={group.agingBuckets}
                openBalanceAmount={group.openBalance}
                displayCurrency={receivablesDisplayCurrency}
              />
            ))}
          </div>
        ) : null}
      </DashboardZone>
    </div>
  );
}

export const ReceivablesChartsPanel = memo(ReceivablesChartsPanelComponent);
